import React, { useEffect, useRef } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { debounce } from 'lodash';
import getDisplayName from 'react-display-name';

import { type IHostProps } from '@wix/yoshi-flow-editor';

export type WithLayoutUpdate = {
  host: IHostProps;
};

export const withLayoutUpdate = <P extends WithLayoutUpdate>(
  WrappedComponent: React.ComponentType<P>,
) => {
  const WithLayoutUpdateComponent: React.FC<P> = (props) => {
    const host = useRef(props.host);
    host.current = props.host;

    useEffect(() => {
      const node = document.getElementById(host.current.id);
      if (!node) {
        return;
      }

      node.style.minWidth = '0';
      let lastHeight = node.offsetHeight;
      const updateLayoutIfNeeded = () => {
        if (lastHeight !== node.offsetHeight) {
          lastHeight = node.offsetHeight;
          host.current.updateLayout();
        }
      };
      const updateLayoutIfNeededDebounced = debounce(updateLayoutIfNeeded, 100, {
        leading: true,
      });

      const observer = new MutationObserver(updateLayoutIfNeeded);
      observer.observe(node, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });

      window.addEventListener('resize', updateLayoutIfNeededDebounced);
      window.addEventListener('transitionend', updateLayoutIfNeeded);

      return () => {
        observer.disconnect();
        window.removeEventListener('resize', updateLayoutIfNeededDebounced);
        window.removeEventListener('transitionend', updateLayoutIfNeeded);
      };
    }, []);

    return <WrappedComponent {...props} />;
  };

  WithLayoutUpdateComponent.displayName = `withLayoutUpdate(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(WithLayoutUpdateComponent, WrappedComponent);

  return WithLayoutUpdateComponent;
};
